import { z } from 'zod';

/*
 * Contacts: one card per person or company, with every role they have for the
 * agency (owner of one house, buyer of another). Rules shared by the API and the
 * web forms. Messages are shown to users (Spanish).
 */

export const CONTACT_KINDS = { person: 'Persona', company: 'Empresa' } as const;
export type ContactKind = keyof typeof CONTACT_KINDS;

/** Roles ("papeles"): assigned by hand or automatically (owner of a property...). */
export const CONTACT_TYPES = {
  owner: 'Propietario',
  buyer: 'Comprador',
  tenant: 'Inquilino',
  lead: 'Lead',
  external_broker: 'Corredor externo',
} as const;
export type ContactType = keyof typeof CONTACT_TYPES;

/** "A is <kind> of B". Spouse and partner read the same both ways. */
export const RELATION_KINDS = {
  spouse: { label: 'Cónyuge', inverse: 'Cónyuge' },
  partner: { label: 'Socio', inverse: 'Socio' },
  attorney: { label: 'Apoderado de', inverse: 'Representado por (apoderado)' },
  representative: { label: 'Representante de', inverse: 'Representada por' },
} as const;
export type RelationKind = keyof typeof RELATION_KINDS;

export const PHONE_LABELS = {
  mobile: 'Móvil',
  home: 'Casa',
  work: 'Trabajo',
  other: 'Otro',
} as const;
export const EMAIL_LABELS = { personal: 'Personal', work: 'Trabajo', other: 'Otro' } as const;

export const TAG_COLORS = [
  'slate',
  'red',
  'orange',
  'amber',
  'green',
  'teal',
  'blue',
  'violet',
  'pink',
] as const;
export type TagColor = (typeof TAG_COLORS)[number];

/** Channels every new agency starts with (editable). */
export const DEFAULT_CHANNELS = [
  'Sitio web',
  'Facebook',
  'Instagram',
  'Meta',
  'Encuentra24',
  'Referido',
  'Llamada',
  'WhatsApp',
  'Visita a la oficina',
  'Rótulo',
  'Otro',
] as const;

/**
 * Phone to E.164. Eight digits starting with 2, 6 or 7 are Salvadoran (+503);
 * anything else must include its country code (+1 555...). null if invalid.
 */
export function normalizePhone(value: string): string | null {
  const trimmed = value.trim();
  const digits = trimmed.replace(/\D/g, '');
  if (!trimmed.startsWith('+') && /^[267]\d{7}$/.test(digits)) return `+503${digits}`;
  if (/^503[267]\d{7}$/.test(digits)) return `+${digits}`;
  if (trimmed.startsWith('+') && /^[1-9]\d{7,14}$/.test(digits)) return `+${digits}`;
  return null;
}

/**
 * DUI (Documento Único de Identidad): 8 digits and a check digit. The check digit is
 * 10 minus the weighted sum (weights 9..2) modulo 10, where 10 becomes 0.
 */
export function isValidDui(value: string): boolean {
  const match = /^(\d{8})-?(\d)$/.exec(value.trim());
  if (!match?.[1] || !match[2]) return false;
  const sum = Array.from(match[1]).reduce((acc, digit, i) => acc + Number(digit) * (9 - i), 0);
  return (10 - (sum % 10)) % 10 === Number(match[2]);
}

/** "01234567-8" whatever the input format (assumes it is valid). */
export function formatDui(value: string): string {
  const digits = value.replace(/\D/g, '');
  return `${digits.slice(0, 8)}-${digits.slice(8)}`;
}

const phone = z.strictObject({
  number: z
    .string()
    .trim()
    .max(30)
    .transform((value, ctx) => {
      const normalized = normalizePhone(value);
      if (!normalized) {
        ctx.addIssue({
          code: 'custom',
          message: 'Teléfono no válido: 8 dígitos de El Salvador, o con código de país (+1 555...)',
        });
        return z.NEVER;
      }
      return normalized;
    }),
  label: z.enum(Object.keys(PHONE_LABELS) as [keyof typeof PHONE_LABELS]).default('mobile'),
  whatsapp: z.boolean().default(true),
});

const email = z.strictObject({
  address: z.string().trim().toLowerCase().pipe(z.email('Correo no válido').max(254)),
  label: z.enum(Object.keys(EMAIL_LABELS) as [keyof typeof EMAIL_LABELS]).default('personal'),
});

const optionalText = (max: number) =>
  z
    .string()
    .trim()
    .max(max, `Máximo ${String(max)} caracteres`)
    .transform((v) => (v === '' ? null : v))
    .nullable();

/** Create and edit share the same body; the first phone/email is the primary one. */
export const contactSchema = z
  .strictObject({
    kind: z.enum(['person', 'company']).default('person'),
    name: z.string().trim().min(1, 'El nombre es obligatorio').max(200),
    phones: z.array(phone).max(5).default([]),
    emails: z.array(email).max(5).default([]),
    /** Optional until a closing (data minimization). Sensitive: encrypted at rest. */
    nationalId: optionalText(12)
      .refine(
        (v) => v === null || isValidDui(v),
        'DUI no válido (8 dígitos, guion y dígito verificador)',
      )
      .optional(),
    /** Exact address. Sensitive: encrypted at rest, revealed on demand. */
    address: optionalText(300).optional(),
    /** Where they live or work: the district (Santa Tecla...). */
    districtId: z.number().int().positive().nullable().optional(),
    channelId: z.uuid('Canal no válido').nullable().optional(),
    assignedMembershipId: z.uuid('Agente no válido').optional(),
    types: z
      .array(z.enum(Object.keys(CONTACT_TYPES) as [ContactType]))
      .max(5)
      .default([]),
    privateNotes: optionalText(5000).optional(),
  })
  .refine((v) => v.phones.length > 0 || v.emails.length > 0, {
    message: 'Escribe al menos un teléfono o un correo',
    path: ['phones'],
  });
export type ContactInput = z.input<typeof contactSchema>;
export type ContactData = z.output<typeof contactSchema>;

export const contactListQuerySchema = z.strictObject({
  q: z.string().trim().max(100).optional(),
  type: z.enum(Object.keys(CONTACT_TYPES) as [ContactType]).optional(),
  tagId: z.uuid().optional(),
  channelId: z.uuid().optional(),
  assignedMembershipId: z.uuid().optional(),
  createdFrom: z.iso.date().optional(),
  createdTo: z.iso.date().optional(),
  /** Imported contacts that have not received the privacy notice yet. */
  noticePending: z.enum(['true']).optional(),
  page: z.coerce.number().int().min(1).max(10_000).default(1),
  pageSize: z.coerce.number().int().min(1).max(100).default(25),
});
export type ContactListQuery = z.infer<typeof contactListQuerySchema>;

/** Is there already a contact with this phone, email or DUI? (before creating one) */
export const contactDuplicateQuerySchema = z.strictObject({
  phones: z.array(z.string().max(30)).max(5).default([]),
  emails: z.array(z.string().max(254)).max(5).default([]),
  nationalId: z.string().max(12).nullable().optional(),
  excludeId: z.uuid().optional(),
});
export type ContactDuplicateQuery = z.input<typeof contactDuplicateQuerySchema>;

export const contactRelationSchema = z.strictObject({
  relatedContactId: z.uuid('Contacto no válido'),
  kind: z.enum(Object.keys(RELATION_KINDS) as [RelationKind]),
});
export type ContactRelationInput = z.infer<typeof contactRelationSchema>;

export const tagSchema = z.strictObject({
  name: z.string().trim().min(1, 'El nombre es obligatorio').max(40),
  color: z.enum(TAG_COLORS).default('blue'),
});
export type TagInput = z.input<typeof tagSchema>;

export const bulkTagSchema = z.strictObject({
  contactIds: z.array(z.uuid()).min(1).max(500),
  tagId: z.uuid(),
  action: z.enum(['add', 'remove']),
});
export type BulkTagInput = z.infer<typeof bulkTagSchema>;

export const mergeContactsSchema = z.strictObject({
  /** The duplicate: its phones, emails, roles, relations and tags move to the kept one. */
  duplicateId: z.uuid(),
});
export type MergeContactsInput = z.infer<typeof mergeContactsSchema>;

export const channelSchema = z.strictObject({
  name: z.string().trim().min(1, 'El nombre es obligatorio').max(60),
  active: z.boolean().default(true),
});
export type ChannelInput = z.input<typeof channelSchema>;
